import { useEffect, useState, type ComponentType } from 'react';

/**
 * A part of the shell that adds to the screen and never stands in a gap (an organisation's logo
 * beside the work area's name, a refused control's touch hint), in its own chunk so the launch
 * entry does not carry it (`ENTRY_BYTES_BUDGET`). It draws nothing until its chunk is in, and
 * nothing when the chunk does not load (offline): the screen is whole without it, so a failure is
 * never thrown to the route's error screen. A later mount asks for the chunk again.
 *
 * `preload()` asks for the chunk before anything mounts it (T-0968): a part drawn only after a
 * click, such as an open menu's options, is read once the launch is done and on the first hover, so
 * it is in by the time the menu opens and draws in that same commit.
 */
export type LazyQuietType<P extends object> = ComponentType<P> & { preload: () => void };

export function lazyQuiet<M, P extends object>(
  load: () => Promise<M>,
  pick: (module: M) => ComponentType<P>,
): LazyQuietType<P> {
  let loaded: ComponentType<P> | null = null;
  let loading: Promise<void> | null = null;
  const waiting = new Set<() => void>();
  const start = () => {
    if (loaded) return;
    loading ??= load().then(
      (module) => {
        loaded = pick(module);
        waiting.forEach((wake) => wake());
      },
      () => {
        loading = null;
      },
    );
  };
  function Quiet(props: P) {
    const [, drawn] = useState(0);
    useEffect(() => {
      if (loaded) return undefined;
      const draw = () => drawn((count) => count + 1);
      waiting.add(draw);
      start();
      return () => void waiting.delete(draw);
    }, []);
    const Loaded = loaded;
    return Loaded ? <Loaded {...props} /> : null;
  }
  return Object.assign(Quiet, { preload: start });
}
